import { Pencil } from "lucide-react";

/**
 * その場で編集できるテキストセル。
 *
 * ngtg_msc の `EditableLinkText` に対応する。値は**青字＋下線のクリック可能
 * テキスト**で見せ、クリックで 1 項目だけの編集ダイアログを開く（開くのは
 * 呼び出し側の責務。ここは表示とクリックだけ）。
 *
 * セル自身は状態を持たない。一覧は行数 × 列数だけ描かれるため、セルごとに
 * ダイアログや入力状態を抱えると重くなる（msc も同じ理由で状態を持たせていない）。
 *
 * 未設定は淡色の「—」で表示し、**リンク装飾は付けない**（下線が「—」と重なって
 * 潰れるうえ、値が無いのにリンクに見えるため）。
 *
 * **値があるときも鉛筆アイコンを添える。** 青字＋下線のクリックできる値には
 * 「別の画面へ移る」ものもあり（[[LinkCell]]）、印が無いと押すまでどちらか
 * 分からない。鉛筆＝その場で直せる、右上向きの矢印＝画面が移る、で見分ける。
 *
 * 権限が無いときはただのテキストとして表示する。押しても何も起きないリンクを
 * 出さないため。
 */
export default function EditableTextCell({
  label,
  value,
  canWrite,
  tone = "normal",
  onEdit,
}: {
  /** 項目名。読み上げ用のラベルに使う（値だけでは何の項目か分からないため）。 */
  label: string;
  value: string | null | undefined;
  canWrite: boolean;
  /**
   * 値そのものに警告の意味があるとき（納期を過ぎた日付など）は `danger`、
   * 同じ表の中でさらに強く出したいもの（回答期限の超過）は `danger-strong`。
   *
   * 編集できるようにしたせいで色の意味が消えないようにするためのもの。
   * 赤いまま押せる値にする。
   */
  tone?: "normal" | "danger" | "danger-strong";
  onEdit: () => void;
}) {
  const text = value?.trim() ? value.trim() : null;
  const danger = tone === "danger" || tone === "danger-strong";
  const color = danger
    ? `text-danger hover:text-danger-500 ${tone === "danger-strong" ? "font-bold" : ""}`
    : "text-brand-500 hover:text-brand-600";

  if (!canWrite) {
    return text ? (
      <span
        className={
          danger ? `text-danger ${tone === "danger-strong" ? "font-bold" : ""}` : ""
        }
      >
        {text}
      </span>
    ) : (
      <span className="text-default-400">—</span>
    );
  }

  if (!text) {
    return (
      <button
        type="button"
        onClick={onEdit}
        aria-label={`${label}を編集`}
        className="inline-flex cursor-pointer items-center gap-1 rounded px-1 py-0.5 hover:bg-default-100"
      >
        <span className="text-default-400">—</span>
        <Pencil size={12} className="text-brand-500" />
      </button>
    );
  }

  return (
    <button
      type="button"
      onClick={onEdit}
      aria-label={`${label}を編集`}
      className={`inline-flex max-w-full cursor-pointer items-center gap-1 rounded px-1 py-0.5 text-left ${color}`}
    >
      <span className="truncate underline underline-offset-2">{text}</span>
      <Pencil size={12} className="shrink-0" aria-hidden />
    </button>
  );
}
